import { useCallback, useEffect, useRef, useState } from 'react';
import { useQcpIssueTicket } from '@/hooks/useQcpIssueTicket';
import { ServerEvents, type InviteAcceptedPayload } from '@/lib/qcp/protocol';
import { qcpIdempotencyKey, qcpStatusOf } from '@/lib/qcp/rest';
import { openQcpSocket } from '@/lib/qcp/socket';
import { limuloApi } from '@/services/limuloApi';
import { useAppDispatch } from '@/store/hooks';

export type CpInviteStatus = 'idle' | 'creating' | 'pending' | 'accepted';

/** What `create()` resolves to / the pending invite the sheet renders. */
export interface CpPendingInvite {
  code: string;
  /** Epoch ms at which the invite expires (for the countdown): the server sends a NUMBER, `Date.parse(number)` is NaN. */
  expiresAt: number;
}

/**
 * The creator-side resolution: the freshly created match + the acceptor info.
 * The creator is always seat A (the server pushes seat:'A' in InviteAccepted).
 */
export interface CpInviteAccepted {
  matchId: string;
  /** seat is always 'A' for the creator (the acceptor is 'B'). */
  seat: 'A';
  opponent: InviteAcceptedPayload['opponent'];
}

export interface CpInviteController {
  status: CpInviteStatus;
  /** The live invite once created (code + expiry), else null. */
  pending: CpPendingInvite | null;
  /** Set when the creator's socket receives `qcp:invite:accepted`. */
  accepted: CpInviteAccepted | null;
  /** 'limit' = 409 invite_limit_reached; 'failed' = any other create error. */
  error: 'limit' | 'failed' | null;
  /** `invite.create` on the Limulo BFF → stash {code, expiresAt} and open the listen socket. */
  create: () => void;
  /** `invite.revoke` on the Limulo BFF (creator only) and reset to idle. */
  cancel: (code: string) => void;
}

/**
 * Drives the CREATOR side of a friend-challenge invite from the TMA.
 *
 * Lifecycle mirrors `useInvite` (QGammon): the listen-only Socket.IO
 * connection is opened ONLY while an invite is pending. `create()` POSTs
 * `invite.create` of the BFF, and on success opens a handshake-auth-only socket
 * (no `match:join`) — the `qcp:invite:accepted` event is fanned out by the
 * QCP gameserver to every one of the creator's sockets (UserSocketIndex), so a
 * listen-only socket receives it.  When it arrives we stash `accepted` and let
 * the CALLER navigate to `/chinesepoker/match/:matchId` with seat A context.
 * `cancel()` DELETEs the code and tears the socket down. The socket also closes
 * on accept and on unmount.
 *
 * T1b: the invite REST goes through the Limulo BFF (`/v1/games/chinesepoker/invites`, session of Limulo, no Bearer for QCP); the socket
 * opens with a Limulo TICKET (purpose `invite`).
 *
 * 🔴 `invite.create` REQUIRES an `idempotencyKey` in the BODY: chosen ONCE per user action and reused IDENTICAL on every new attempt of
 * the same action (a fresh key per request would duplicate an invite whose first answer got lost). It is dropped once the invite exists
 * and after a DEFINITIVE refusal (a 4xx would be replayed by the server for the same key).
 *
 * 409 (the BFF flattens the app code: on this route it can only be the invite limit): sets error:'limit'; caller surfaces the message.
 *
 * OFC has no matchLength picker — create() takes no arguments.
 */
export function useCpInvite(): CpInviteController {
  // The close of the open socket (`openQcpSocket`), or null when none is open.
  const closeSocketRef = useRef<(() => void) | null>(null);
  const issueTicket = useQcpIssueTicket();
  const dispatch = useAppDispatch();
  // The key of the create in flight / to retry (see above).
  const keyRef = useRef<string | null>(null);
  const [status, setStatus] = useState<CpInviteStatus>('idle');
  const [pending, setPending] = useState<CpPendingInvite | null>(null);
  const [accepted, setAccepted] = useState<CpInviteAccepted | null>(null);
  const [error, setError] = useState<CpInviteController['error']>(null);

  const closeSocket = useCallback(() => {
    closeSocketRef.current?.();
    closeSocketRef.current = null;
  }, []);

  useEffect(() => {
    // The close also stops a socket whose ticket has not arrived yet, so none leaks past unmount.
    return () => {
      closeSocket();
    };
  }, [closeSocket]);

  const openSocket = useCallback(() => {
    if (closeSocketRef.current) return;
    closeSocketRef.current = openQcpSocket({
      purpose: 'invite',
      issueTicket,
      // No socket (ticket refused, capability off, address not allowed, handshake refused): the invite cannot be heard.
      onFail: () => {
        closeSocketRef.current = null;
        setError('failed');
        setStatus('idle');
      },
      onSocket: (socket) => {
        socket.on(ServerEvents.InviteAccepted, (p: InviteAcceptedPayload) => {
          setAccepted({ matchId: p.matchId, seat: p.seat, opponent: p.opponent });
          setStatus('accepted');
          // The invite is consumed — stop listening; the caller navigates next.
          closeSocket();
        });
      },
    });
  }, [closeSocket, issueTicket]);

  const create = useCallback(() => {
    setError(null);
    setAccepted(null);
    setStatus('creating');
    keyRef.current ??= qcpIdempotencyKey();
    const richiesta = dispatch(limuloApi.endpoints.createChinesePokerInvite.initiate({ idempotencyKey: keyRef.current }));
    void (async () => {
      try {
        const invite = await richiesta.unwrap();
        if (invite === null) throw new Error('unreadable');
        keyRef.current = null; // the action is done: the next create is a new one
        setPending({ code: invite.code, expiresAt: invite.expiresAt });
        setStatus('pending');
        openSocket();
      } catch (e) {
        const http = qcpStatusOf(e);
        // A definitive refusal (4xx, but 424 = a fault of the resolver) is not retried with the same key.
        if (http !== null && http >= 400 && http < 500 && http !== 424) keyRef.current = null;
        setError(http === 409 ? 'limit' : 'failed');
        setStatus('idle');
      } finally {
        richiesta.reset();
      }
    })();
  }, [dispatch, openSocket]);

  const cancel = useCallback(
    (code: string) => {
      setStatus('idle');
      setPending(null);
      setAccepted(null);
      setError(null);
      closeSocket();
      // Best-effort: the code expires by itself.
      const richiesta = dispatch(limuloApi.endpoints.revokeChinesePokerInvite.initiate(code));
      void richiesta.unwrap().catch(() => undefined).finally(() => richiesta.reset());
    },
    [closeSocket, dispatch],
  );

  return { status, pending, accepted, error, create, cancel };
}
